import { useState, useRef, useEffect } from 'react';
import { IconButton, Spinner } from '@blacknbrownstudios/ui';
import { useChatStore } from '../stores/chat';
import { ServerSelector } from './ServerSelector';

export function MessageInput() {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const {
    sendMessage,
    isWaiting,
    isConnected,
    availableServers,
    enabledServers,
    toggleServer,
    isLoadingServers,
    serverError,
    activePreset,
    setPreset,
  } = useChatStore();

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  const handleSubmit = () => {
    const trimmed = input.trim();
    if (!trimmed || isWaiting || !isConnected) return;

    sendMessage(trimmed);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-neutral-800 bg-neutral-900 p-4">
      <div className="max-w-3xl mx-auto">
        <div className="relative flex items-end bg-neutral-800 rounded-xl border border-neutral-700 focus-within:border-primary-500 transition-colors">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isConnected ? 'Send a message...' : 'Connecting...'}
            disabled={!isConnected}
            rows={1}
            className="flex-1 bg-transparent text-white placeholder-neutral-500 px-4 py-3 resize-none focus:outline-none text-sm leading-relaxed"
            style={{ maxHeight: '200px' }}
          />

          <IconButton
            onClick={handleSubmit}
            disabled={!input.trim() || isWaiting || !isConnected}
            color="primary"
            size="medium"
            className="m-1.5 bg-primary-600 hover:bg-primary-700 disabled:bg-neutral-700 rounded-lg"
            aria-label={isWaiting ? 'Sending...' : 'Send message'}
          >
            {isWaiting ? (
              <Spinner size="small" color="inherit" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            )}
          </IconButton>
        </div>

        <div className="mt-2 flex items-center justify-between text-xs text-neutral-500">
          <ServerSelector
            availableServers={availableServers}
            enabledServers={enabledServers}
            onToggle={toggleServer}
            isLoading={isLoadingServers}
            error={serverError}
            activePreset={activePreset}
            onPresetChange={setPreset}
          />
          <div>
            Press <kbd className="px-1.5 py-0.5 bg-neutral-800 rounded text-neutral-400">Enter</kbd>{' '}
            to send,{' '}
            <kbd className="px-1.5 py-0.5 bg-neutral-800 rounded text-neutral-400">
              Shift + Enter
            </kbd>{' '}
            for new line
          </div>
        </div>
      </div>
    </div>
  );
}
